import {
  HoldGate,
  STING_END_MS,
  STING_HOLD_END_MS,
  STING_RING_END_MS,
  STING_TEAR_END_MS,
  stingBeatAt,
} from '@aeonia/gaia-client';

// The plain-drawing sting, ported from
// docs/glasses-client/effects-lab/harness-canvas2d/app.js.
//
// It runs when the browser gives us no WebGL2 context. It draws the same four
// beats with ordinary 2D canvas calls: a strip tear, a blurred copy of the mark
// as the glow, a ring pulse, then a fade. Everything composites with 'lighter'
// so light only ever gets added to black.

const W = 600;
const H = 600;
const MARK_W = 480;
const MARK_H = 240;
const MARK_X = 60;
const MARK_Y = 180;
const SLICES = 18;

/** Fixed-seed per-strip offsets, so every run tears identically. */
function seedJitter(): number[] {
  const jitter: number[] = [];
  let seed = 1337;
  for (let i = 0; i < SLICES; i += 1) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    jitter.push((seed / 0x7fffffff) * 2 - 1);
  }
  return jitter;
}

function offscreen(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** A copy of the mark multiplied by one flat colour, for the split-colour tear. */
function tinted(markImage: CanvasImageSource, color: string): HTMLCanvasElement {
  const canvas = offscreen(MARK_W, MARK_H);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.drawImage(markImage, 0, 0);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, MARK_W, MARK_H);
  return canvas;
}

export type Canvas2dStingAssets = {
  glow: HTMLCanvasElement;
  tintA: HTMLCanvasElement;
  tintB: HTMLCanvasElement;
  jitter: number[];
};

/** Blur once at setup. A per-frame shadowBlur is far too slow on the glasses. */
function buildAssets(markImage: CanvasImageSource): Canvas2dStingAssets {
  const glow = offscreen(MARK_W, MARK_H);
  const ctx = glow.getContext('2d');
  if (ctx) {
    ctx.filter = 'blur(4px)';
    ctx.drawImage(markImage, 0, 0);
    ctx.filter = 'none';
  }
  return {
    glow,
    tintA: tinted(markImage, '#5FD8D0'),
    tintB: tinted(markImage, '#A862C8'),
    jitter: seedJitter(),
  };
}

function drawMark(
  ctx: CanvasRenderingContext2D,
  markImage: CanvasImageSource,
  assets: Canvas2dStingAssets,
  alpha: number,
): void {
  ctx.globalAlpha = alpha * 0.55;
  ctx.drawImage(assets.glow, MARK_X, MARK_Y);
  ctx.globalAlpha = alpha;
  ctx.drawImage(markImage, MARK_X, MARK_Y);
}

/**
 * Draw one frame of the sting into an already-cleared 2D context.
 *
 * `elapsedMs` is milliseconds since the sting started and `released` is the
 * hold gate, exactly as `stingBeatAt` reads them.
 */
export function drawCanvas2dStingFrame(
  ctx: CanvasRenderingContext2D,
  markImage: CanvasImageSource,
  assets: Canvas2dStingAssets,
  elapsedMs: number,
  released: boolean,
): void {
  const frame = stingBeatAt(elapsedMs, released);
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 1;

  if (frame.beat === 'tear') {
    const p = frame.progress;
    const decay = (1 - p) * (1 - p);
    const bandHeight = MARK_H / SLICES;

    // Horizontal strips, each shoved sideways by a decaying random offset.
    for (let i = 0; i < SLICES; i += 1) {
      const offset = assets.jitter[i] * decay * 70;
      ctx.globalAlpha = 0.3 + 0.7 * p;
      ctx.drawImage(
        markImage,
        0,
        i * bandHeight,
        MARK_W,
        bandHeight,
        MARK_X + offset,
        MARK_Y + i * bandHeight,
        MARK_W,
        bandHeight,
      );
    }

    // Two offset tinted copies give the colour split.
    const split = decay * 22;
    ctx.globalAlpha = 0.45 * (1 - p);
    ctx.drawImage(assets.tintA, MARK_X - split, MARK_Y);
    ctx.drawImage(assets.tintB, MARK_X + split, MARK_Y);

    // A resolve front sweeps left to right, revealing the crisp mark behind it.
    const front = MARK_W * (p * p * (3 - 2 * p));
    ctx.save();
    ctx.beginPath();
    ctx.rect(MARK_X, MARK_Y, front, MARK_H);
    ctx.clip();
    drawMark(ctx, markImage, assets, 1);
    ctx.restore();
  } else if (frame.beat === 'hold') {
    drawMark(ctx, markImage, assets, 1);
  } else if (frame.beat === 'ring') {
    const u = frame.progress;
    drawMark(ctx, markImage, assets, 1);
    const radius = u * 0.9 * (W / 2);
    ctx.strokeStyle = '#B6E3E2';
    ctx.lineWidth = 4;
    ctx.globalAlpha = 1 - u;
    ctx.beginPath();
    ctx.arc(W / 2, H / 2, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.45 * (1 - u);
    ctx.beginPath();
    ctx.arc(W / 2, H / 2, radius * 0.62, 0, Math.PI * 2);
    ctx.stroke();
  } else if (frame.beat === 'fade') {
    drawMark(ctx, markImage, assets, Math.max(0, 1 - frame.progress));
  }

  ctx.globalAlpha = 1;
}

/**
 * Play the whole sting on a plain 2D canvas.
 *
 * The animation parks at the end of the hold until `hold` is released, and
 * cancels its animation frame while parked so an idle page asks for no frames.
 * Returns a cancel function that stops the run without calling `onDone`.
 */
export function playCanvas2dSting(
  canvas: HTMLCanvasElement,
  markImage: CanvasImageSource,
  onDone: () => void,
  hold: HoldGate = (() => {
    const gate = new HoldGate();
    gate.release();
    return gate;
  })(),
): () => void {
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    onDone();
    return () => {};
  }

  const assets = buildAssets(markImage);
  let startedAt = performance.now();
  let handle = 0;
  let stopped = false;

  function clear(): void {
    if (!ctx) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, W, H);
  }

  function finish(): void {
    stopped = true;
    if (handle) cancelAnimationFrame(handle);
    handle = 0;
    clear();
    onDone();
  }

  function resume(): void {
    if (stopped) return;
    // Restart the clock at the hold boundary so no time passed while parked.
    startedAt = performance.now() - STING_HOLD_END_MS;
    handle = requestAnimationFrame(frame);
  }

  function frame(now: number): void {
    if (stopped) return;
    handle = 0;
    // Time comes straight from the clock, never from an accumulated delta.
    const elapsed = now - startedAt;

    if (elapsed >= STING_HOLD_END_MS && !hold.released) {
      // Parked: draw nothing new and ask for no more frames.
      hold.whenReleased(resume);
      return;
    }

    if (elapsed >= STING_END_MS) {
      finish();
      return;
    }

    clear();
    drawCanvas2dStingFrame(ctx!, markImage, assets, elapsed, hold.released);
    handle = requestAnimationFrame(frame);
  }

  handle = requestAnimationFrame(frame);

  return () => {
    stopped = true;
    if (handle) cancelAnimationFrame(handle);
    handle = 0;
  };
}

export { STING_END_MS, STING_HOLD_END_MS, STING_RING_END_MS, STING_TEAR_END_MS };
